/**
 * @fileoverview Security and utility functions for hashing, generating unique IDs, parsing JSON, and more.
 * @module securityUtils
 */

/**
 * Hashes an email address using SHA-256.
 * @param {string} email - The email address to hash.
 * @returns {Promise<string>} A promise that resolves to the hashed email.
 */
export const hashEmail = async (email) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Generates a unique ID.
 * @returns {string} A unique ID.
 */
export const generateUniqueId = () => {
  return `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Gets the current page name from the URL.
 * @returns {string} The current page name.
 */
export const getPageName = () => {
  const urlSegments = window.location.pathname.split("/");
  return (
    urlSegments[urlSegments.length - 2] || urlSegments[urlSegments.length - 1]
  );
};

/**
 * Safely parses JSON, returning an empty object if parsing fails.
 * @param {string} jsonString - The JSON string to parse.
 * @returns {Object} The parsed object or an empty object if parsing fails.
 */
export const safeJSONParse = (jsonString) => {
  if (!jsonString) return {};
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    Logger.error("Failed to parse JSON:", e);
    return {};
  }
};

/**
 * Debounce function to limit the rate at which a function can fire.
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to wait.
 * @returns {Function} The debounced function.
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};